import React from "react";
import { observer } from 'mobx-react';
import { Link } from "react-router-dom";
import Hls from "hls.js";
import Loading from "react-simple-loading";
import { isMobile } from "react-device-detect";
import AnchorLiveFeedPlayerControls from "./AnchorLiveFeedPlayerControls/AnchorLiveFeedPlayerControls";
import Alert from "../../generalComponents/Alert";
import { hlsErrorIds } from "../../../constants/hlsErrorsIds";
import {status} from "../../../constants/sfwStatus";
import StreamViewPlayer from "../../StreamViewPlayer/StreamViewPlayer";
import * as Sentry from '@sentry/browser';
import GroupShow from './GroupShow/GroupShow';
import GroupShowStore from '../../../store/GroupShowStore';
import GroupShowOverlay from './GroupShowOverlay/GroupShowOverlay';
import { isSubscribedToGroupShowAnchor } from '../../../utils/helpers';
import GroupShowNotAvailableOverlay from './GroupShowNotAvailableOverlay/GroupShowNotAvailableOverlay';
import TulumStreamNotAvailableOverlay from './TulumStreamNotAvailableOverlay/TulumStreamNotAvailableOverlay';

import "./css/AnchorLiveFeedPlayer.css";
import "./css/AnchorLiveFeedPlayer.mobile.css";
import "./css/safari-fullscreen-fix.css";

@observer
class AnchorLiveFeedPlayer extends React.Component {
  hls = {};
  videoRef = React.createRef();
  videoWrapRef = React.createRef();
  loadSourceRetries = 5;

  state = {
    muted: false,
    volume: 1,
    showPlay: false,
    loading: true,
    streamErrorMessage: "",
    orientation: "portrait",
    isAlertVisible: false,
    alertContent: "",
  };

  loadHLS = (hls, url, videoRef) => {
    hls.loadSource(url);
    hls.attachMedia(videoRef);
  };
  componentDidMount(){
    Sentry.configureScope(scope => {
      scope.setTag('NN-Page', 'anchor');
      scope.setTag('NN-Page Anchor ID', this.props.anchorId);
      scope.setTag('NN-Page Anchor Username', this.props.anchorUsername);
    });
  }

  componentWillReceiveProps(newProps) {
    if (newProps.broadcasterClosedSession) {
      setTimeout(() => {
        this.props.uiStore.notify({
          content: {
            type: 'anchorStoppedBroadcast',
            anchorId: this.props.anchorId,
            anchorName: this.props.title,
            anchorUserName: this.props.anchorUsername
          }
        })
        this.setState({ streamErrorMessage: 'The anchor has ended the broadcasting session' }, this.closeGroupShow);
      }, 12000);
    }
    if (newProps.spotlighterClosedSession) {
      setTimeout(() => {
        this.props.uiStore.notify({
          content: {
            type: 'anchorStoppedBroadcast',
            anchorId: this.props.anchorId,
            anchorName: this.props.title,
            anchorUserName: this.props.anchorUsername
          }
        })
        this.setState({ streamErrorMessage: 'The spotlighting session has ended' }, this.closeGroupShow);
      }, 6000);
    }
  }

  closeGroupShow = () => {
    if (GroupShowStore.isInGroupShow) {
      setTimeout(() => {
        GroupShowStore.setInGroupShow(false);
        GroupShowStore.resetStreamVolume();
      }, 5000);
    }
  }

  handleHLSError = error => {
    const { broadcasterClosedSession, spotlighterClosedSession, onTulumStreamError } = this.props;
    const { type, details, fatal } = error;
    const errorId = hlsErrorIds[details];

    // Display only fatal hls errors during the broadcast
    const showError = fatal && errorId && !broadcasterClosedSession && !spotlighterClosedSession;
    if (showError) {
      this.displayAlert(type, errorId);
    }

    if (onTulumStreamError) {
      onTulumStreamError(error);
    }
  }
  
  startBroadcast = () => {
    const { anchorVideoUrl } = this.props;
    !isMobile && this.customVolume(1);
    const video = document.getElementById("video");
    if (Hls.isSupported()) {
      this.hls = new Hls();
      this.loadHLS(this.hls, anchorVideoUrl.streamViews[1].url, video);
      this.hls.on(Hls.Events.ERROR, (_, error) => {
        

        if (this.loadSourceRetries) {
          this.loadSourceRetries && this.props.onNetworkError();
          this.loadSourceRetries--;
        }
      });
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.loadSourceRetries = 5;
        video
          .play()
          .then(() => {
            this.setState({
              showPlay: false,
              loading: false,
            });
          })
          .catch(() => {
            this.setState({
              showPlay: true,
              loading: false,
            });
          });
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = anchorVideoUrl;
      this.setState({
        showPlay: true,
        loading: false,
      });
    }
  };

  displayAlert = (errorType, errorId) => {
    this.setState({
      isAlertVisible: true,
      alertContent: `Error type: ${errorType}. Error ID: ${errorId}`,
    });
  };

  toggleFullscreen = () => {
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (this.videoWrapRef.current.requestFullscreen) {
        this.videoWrapRef.current.requestFullscreen();
      } else if (this.videoWrapRef.current.msRequestFullscreen) {
        this.videoWrapRef.current.msRequestFullscreen();
      } else if (this.videoWrapRef.current.mozRequestFullScreen) {
        this.videoWrapRef.current.mozRequestFullScreen();
      } else if (this.videoWrapRef.current.webkitRequestFullscreen) {
        this.videoWrapRef.current.webkitRequestFullscreen(
          Element.ALLOW_KEYBOARD_INPUT
        );
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  displayOfflineOverlay = () => {
    const { streamErrorMessage } = this.state;
    this.loadSourceRetries = null;
    return (
      <div className="anchor-ended-session-overlay">
        <p>{streamErrorMessage}</p>
      </div>
    );
  };

  hideAlert = () => {
    this.setState({
      isAlertVisible: false,
      alertContent: "",
    });
  };

  render() {
    const {
      anchorId,
      safeForWatchStatus,
      isAnchor,
      broadcasterClosedSession,
      anchorVideoUrl,
      isAlreadySubscribedToAnchor,
      nsfw,
      user,
      anchorUsername,
      slug,
      anchorName,
      setTippingModal,
      isBanned,
      spotlighterClosedSession,
      isLivePage,
      hasStreamViews,
      isSpotlighing,
      isSpotlight,
      spotlighedAnchorUsername,
      subscribedToOneOfTheAnchors,
      toggleStreamLoading,
      shouldHideVolumeRange,
      hasStreamError,
      isTulumRealityShow,
      onAllowStreamChangeOnError,
    } = this.props;
    const {
      streamErrorMessage,
      orientation,
      isAlertVisible,
      alertContent,
    } = this.state;

    const isSubscribed = (isAlreadySubscribedToAnchor && isAlreadySubscribedToAnchor.active) || (isAlreadySubscribedToAnchor && isLivePage);
    const canWatchSpotlight = isSpotlight && subscribedToOneOfTheAnchors;

    const shouldStart =  (isSubscribed || canWatchSpotlight || isAnchor) ? true : safeForWatchStatus === status.SFW;

    const shouldShowSubscriveOverlay = (isSubscribed || canWatchSpotlight) ? nsfw && !isAnchor : safeForWatchStatus !== status.SFW && !isAnchor;

    const {
      isInGroupShow,
      transitionToGroupShow,
      groupShowParticipant: { anchorDetails }
    } = GroupShowStore;
    const groupShowHost = {
      anchorName,
      anchorUsername,
      anchorId
    };
    let isSubscribedToGroupShowHost = false;
    let isSubscribedToGroupShowParticipant = false;
    const groupShowFeatureAvailable = user ? user.features.includes('TEMPTED_PRIVATE_SHOW') : false;

    if (isInGroupShow) {
      isSubscribedToGroupShowHost = isSubscribedToGroupShowAnchor(user, anchorId);
      isSubscribedToGroupShowParticipant = isSubscribedToGroupShowAnchor(user, anchorDetails.id);
    }

    const isSubscribedToOneGroupShowAnchor = isSubscribedToGroupShowHost || isSubscribedToGroupShowParticipant;
    const shouldDisplaySubscriptionOverlayLiveShow = (shouldShowSubscriveOverlay && !isInGroupShow && !isSpotlight) || isBanned;
    const shouldDisplaySubscriptionOverlayGroupShow = isInGroupShow && groupShowFeatureAvailable && (!isSubscribedToOneGroupShowAnchor || isBanned);
    const shouldDisplaySubscriptionOverlaySpotlightShow = isSpotlight && !subscribedToOneOfTheAnchors && (nsfw || isBanned);

    return (
      <div className="anchor-live-feed-player" ref={this.videoWrapRef}>
        {shouldDisplaySubscriptionOverlayLiveShow && (
          <div className="anchor-video-player-subscribe-overlay">
            <div className="anchor-video-player-subscribe-message">
              <p>Only subscribers can watch this show</p>
              {!isBanned && <Link
                to={`/account/purchase?_anchor=${anchorId}&_shortUrl=${slug}`}
                className="btn btn-red btn-lg btn-curved"
              >
                Subscribe
              </Link>}
            </div>
          </div>
        )}
        {shouldDisplaySubscriptionOverlayGroupShow && (
          <div className="anchor-video-player-subscribe-overlay">
            <div className="anchor-video-player-subscribe-message">
              <p>Only subscribers can watch this party show</p>
              {!isBanned && <Link
                to={`/account/purchase?_anchor=${anchorId}&_shortUrl=${slug}`}
                className="btn btn-red btn-lg btn-curved"
              >
                Subscribe
              </Link>}
            </div>
          </div>
        )}
        {shouldDisplaySubscriptionOverlaySpotlightShow && (
          <div className="anchor-video-player-subscribe-overlay">
            <div className="anchor-video-player-subscribe-message">
              <p>Only subscribers can watch this spotlight show</p>
              {!isBanned && <Link
                to={`/account/purchase?_anchor=${anchorId}&_shortUrl=${slug}`}
                className="btn btn-red btn-lg btn-curved"
              >
                Subscribe
              </Link>}
            </div>
          </div>
        )}
        {isInGroupShow && groupShowFeatureAvailable && (
          <GroupShow
            isSubscribed={isSubscribedToOneGroupShowAnchor}
            onHLSError={this.handleHLSError}
            user={user}
            groupShowHost={groupShowHost}
            setTippingModal={setTippingModal}
            onLiveSessionEnd={this.props.onLiveSessionEnd}
          />
        )}
        {isInGroupShow && !groupShowFeatureAvailable && (
          <GroupShowNotAvailableOverlay
            anchorName={anchorName}/>
        )}
        {transitionToGroupShow && (
          <GroupShowOverlay/>
        )}
        {hasStreamError && (
          <TulumStreamNotAvailableOverlay/>
        )}
        {streamErrorMessage ? (
          this.displayOfflineOverlay()
        ) : (
          !shouldShowSubscriveOverlay && !isInGroupShow && !isBanned &&(<StreamViewPlayer
            videoId={"myvideo"}
            autoplay={shouldStart}
            videoClass={`anchor-live-feed-video anchor-live-feed-video--${orientation}`}
            streamData={anchorVideoUrl}
            onHLSError={this.handleHLSError}
            onAllowStreamChangeOnError={onAllowStreamChangeOnError}
            user={user}
            anchorUsername={anchorUsername}
            hasStreamViews={hasStreamViews}
            isTulumRealityShow={isTulumRealityShow}
            onLiveSessionEnd={this.props.onLiveSessionEnd}
            loadingOverlay={loading =>
              loading &&
              !this.props.broadcasterClosedSession && !this.props.spotlighterClosedSession && (
                <div className="anchor-live-feed-loading">
                  <Loading color="white" />
                </div>
              )
            }
            toggleStreamLoading={toggleStreamLoading}
            controls={({
              muted,
              volume,
              playStream,
              showPlay,
              onVolumeChange,
              onMuteChange,
              disableMute
            }) => (
              <AnchorLiveFeedPlayerControls
                disableMute={disableMute}
                playVideo={playStream}
                showPlay={showPlay}
                broadcasterClosedSession={broadcasterClosedSession}
                spotlighterClosedSession={spotlighterClosedSession}
                mutePlayer={onMuteChange}
                muted={muted}
                onVolumeChange={onVolumeChange}
                volume={volume}
                toggleFullscreen={this.toggleFullscreen}
                shouldHideVolumeRange={shouldHideVolumeRange}
              />
            )}
          />)
        )}
        {isAlertVisible && (
          <Alert onClose={this.hideAlert}>
            {alertContent}
            <p>Please try refreshing the page.</p>
          </Alert>
        )}
      </div>
    );
  }
}

export default AnchorLiveFeedPlayer;
